import React, { useEffect, useState } from "react";
import { TrendingUp, Package, ArrowUpRight, ArrowDownRight, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function StatsCards({ walletData }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState([]);

  useEffect(() => {
    if (walletData) {
      const walletStats = walletData.stats || {};
      
      setStats([
        {
          id: "total_balance",
          title: t('dashboard.stats.total_balance'),
          value: `${parseFloat(walletStats.total_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`,
          change: walletStats.monthly_change || "+0.00%",
          isPositive: (walletStats.monthly_change || "+0.00%").startsWith('+'),
          icon: <Activity className="w-5 h-5 text-emerald-400" />,
          color: "emerald",
          description: t('dashboard.stats.total_balance_desc')
        },
        {
          id: "monthly_gains",
          title: t('dashboard.stats.monthly_gains'),
          value: `${parseFloat(walletStats.monthly_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`,
          change: walletStats.monthly_percent || "+0.00%",
          isPositive: (walletStats.monthly_percent || "+0.00%").startsWith('+'),
          icon: <TrendingUp className="w-5 h-5 text-indigo-400" />,
          color: "indigo",
          description: t('dashboard.stats.monthly_gains_desc')
        },
        {
          id: "wallets",
          title: t('dashboard.stats.wallets'),
          value: walletStats.wallets_count || "0",
          change: t('dashboard.stats.active_label'),
          isPositive: true,
          icon: <Activity className="w-5 h-5 text-cyan-400" />,
          color: "cyan",
          description: t('dashboard.stats.wallets_desc')
        },
        {
          id: "active_packs",
          title: t('dashboard.stats.active_packs'),
          value: walletStats.active_packs || "0",
          change: t('dashboard.stats.in_progress_label'),
          isPositive: true,
          icon: <Package className="w-5 h-5 text-amber-400" />,
          color: "amber",
          description: t('dashboard.stats.active_packs_desc')
        }
      ]);
    }
  }, [walletData, t]);

  const colorMap = {
    emerald: {
      bg: "bg-emerald-500/10",
      border: "border-emerald-500/20",
      text: "text-emerald-400",
      glow: "shadow-emerald-500/10",
      hover: "group-hover:border-emerald-500/40",
      icon: "bg-emerald-500/10 text-emerald-400",
      gradient: "from-emerald-500/20 via-emerald-400/5 to-transparent",
      pill: "bg-emerald-500/10 border-emerald-500/25"
    },
    indigo: {
      bg: "bg-indigo-500/10",
      border: "border-indigo-500/20",
      text: "text-indigo-400",
      glow: "shadow-indigo-500/10",
      hover: "group-hover:border-indigo-500/40",
      icon: "bg-indigo-500/10 text-indigo-400",
      gradient: "from-indigo-500/20 via-indigo-400/5 to-transparent",
      pill: "bg-indigo-500/10 border-indigo-500/25"
    },
    cyan: {
      bg: "bg-cyan-500/10",
      border: "border-cyan-500/20",
      text: "text-cyan-400",
      glow: "shadow-cyan-500/10",
      hover: "group-hover:border-cyan-500/40",
      icon: "bg-cyan-500/10 text-cyan-400",
      gradient: "from-cyan-500/20 via-cyan-400/5 to-transparent",
      pill: "bg-cyan-500/10 border-cyan-500/25"
    },
    amber: {
      bg: "bg-amber-500/10",
      border: "border-amber-500/20",
      text: "text-amber-400",
      glow: "shadow-amber-500/10",
      hover: "group-hover:border-amber-500/40",
      icon: "bg-amber-500/10 text-amber-400",
      gradient: "from-amber-500/20 via-amber-400/5 to-transparent",
      pill: "bg-amber-500/10 border-amber-500/25"
    }
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
      {stats.map((stat, index) => {
        const colors = colorMap[stat.color];
        return (
          <div 
            key={stat.id}
            className={`dashboard-stat-card trading-card bg-gradient-to-br from-slate-900/65 via-slate-900/35 to-transparent p-3 sm:p-4 md:p-5 group flex flex-col justify-between min-h-[120px] sm:min-h-[140px] md:min-h-[180px] relative overflow-hidden border ${colors.border} ${colors.hover}`}
            style={{ animationDelay: `${index * 70}ms` }}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} opacity-70 pointer-events-none`} />
            <div className="dashboard-stat-shimmer" />

            <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
              <div className="flex items-center gap-2 sm:gap-3 relative z-10 min-w-0">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-lg sm:rounded-xl flex items-center justify-center border ${colors.border} ${colors.bg} shadow-lg flex-shrink-0`}>
                  {React.cloneElement(stat.icon, { className: 'w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ' + (stat.icon.props.className?.split(' ').slice(-1)[0] || 'text-emerald-400') })}
                </div>
                <p className="text-[10px] sm:text-[11px] md:text-xs font-semibold text-slate-400 uppercase tracking-wider truncate">
                  {stat.title}
                </p>
              </div>
              
              <div className={`relative z-10 flex items-center gap-1 font-bold text-[8px] sm:text-[9px] md:text-[10px] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border ${colors.pill} bg-slate-950/60 flex-shrink-0 ${
                stat.isPositive ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {stat.isPositive ? <ArrowUpRight size={10} strokeWidth={3} className="sm:w-3 sm:h-3" /> : <ArrowDownRight size={10} strokeWidth={3} className="sm:w-3 sm:h-3" />}
                <span>{stat.change}</span>
              </div>
            </div>

            <div className="relative z-10">
              <h3 className="text-base sm:text-lg md:text-2xl lg:text-[30px] font-black text-white font-outfit tracking-tight leading-none break-words">
                {stat.value}
              </h3>
              <p className="mt-1 sm:mt-2 text-[9px] sm:text-[10px] md:text-[11px] text-slate-400 font-semibold uppercase tracking-wider truncate">
                {stat.description}
              </p>
            </div>

            <div className="relative z-10 mt-2 sm:mt-3 md:mt-4 h-1 sm:h-1.5 w-full rounded-full bg-slate-800/80 overflow-hidden">
              <div className={`h-full w-2/3 rounded-full bg-gradient-to-r ${colors.gradient.replace('/20', '/60').replace('/5', '/40')} animate-soft-pulse`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}